import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CalendarClockIcon, FolderOpenIcon, PackageOpenIcon, TimerIcon } from 'lucide-react';
import { EncabezadoPagina } from '../components/layout/AppLayout';
import { EncabezadoPanel, EstadoVacio, Panel } from '../components/ui/Basicos';
import { ChipDias, PuntoSemaforo } from '../components/ui/Semaforo';
import { ChipMateria } from '../components/ui/Etiquetas';
import { MiniSemana } from '../components/calendario/MiniSemana';
import { useDatos } from '../contexts/DatosContext';
import { abogadoPorId } from '../data/abogados';
import { diasRestantes, fechaCorta } from '../utils/fechas';
import { nivelPorDias } from '../utils/semaforo';
import { esEstaSemana, plazosVigentes } from '../utils/consultas';
import { rutaUbicacion } from '../utils/almacen';
export function Dashboard() {
  const {
    expedientes,
    plazos,
    audiencias,
    sucursalActiva,
    rol,
    sucursales,
    abogados
  } = useDatos();
  const alcance = useMemo(() => sucursalActiva === 'todas' ? 'Todas las sucursales' : sucursales.find(s => s.id === sucursalActiva)?.nombre ?? '', [sucursalActiva, sucursales]);
  const expedientesFiltrados = useMemo(() => expedientes.filter(e => sucursalActiva === 'todas' || e.sucursalId === sucursalActiva), [expedientes, sucursalActiva]);
  const idsEnAlcance = useMemo(() => new Set(expedientesFiltrados.map(e => e.id)), [expedientesFiltrados]);
  const plazosAlcance = useMemo(() => plazosVigentes(plazos).filter(p => idsEnAlcance.has(p.expedienteId)), [plazos, idsEnAlcance]);
  const audienciasSemana = useMemo(() => audiencias.filter(a => idsEnAlcance.has(a.expedienteId) && esEstaSemana(a.fecha)), [audiencias, idsEnAlcance]);
  const prestados = expedientesFiltrados.filter(e => e.prestadoA);
  const tarjetas = [{
    etiqueta: 'Expedientes activos',
    valor: expedientesFiltrados.filter(e => e.estado === 'Activo').length,
    detalle: `${expedientesFiltrados.length} en total en el archivo`,
    icono: FolderOpenIcon,
    a: '/expedientes',
    acento: false
  }, {
    etiqueta: 'Audiencias y citas de la semana',
    valor: audienciasSemana.length,
    detalle: 'Próximos 7 días',
    icono: CalendarClockIcon,
    a: '/calendario',
    acento: false
  }, {
    etiqueta: 'Vencimientos próximos',
    valor: plazosAlcance.filter(p => diasRestantes(p.fechaLimite) <= 10).length,
    detalle: `${plazosAlcance.filter(p => diasRestantes(p.fechaLimite) <= 3).length} en estado crítico`,
    icono: TimerIcon,
    a: '/vencimientos',
    acento: true
  }, {
    etiqueta: 'Prestados sin devolver',
    valor: prestados.length,
    detalle: 'Expedientes físicos fuera de caja',
    icono: PackageOpenIcon,
    a: '/almacenes',
    acento: false
  }];
  const criticos = plazosAlcance.slice(0, 8);
  return <>
      <EncabezadoPagina titulo="Panel de control" descripcion={`${alcance} · vista de ${rol.toLowerCase()}`} acciones={<Link to="/vencimientos" className="inline-flex h-9 items-center gap-2 rounded border border-gris-200 bg-white px-3 text-sm font-medium text-marino-700 transition-colors duration-150 hover:border-marino-300 hover:bg-marino-50" data-id="element-1344" data-mp="3hh3iulsqdfoc">
            Agenda de términos
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1345" data-mp="3vcxhmbzsionq" />
          </Link>} data-id="element-1343" data-mp="3cjttf65cd40i" />

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4" data-id="element-1346" data-mp="464gf84zypj7">
        {tarjetas.map(({
        etiqueta,
        valor,
        detalle,
        icono: Icono,
        a,
        acento
      }) => <Link key={etiqueta} to={a} className={`group rounded-lg border bg-white px-5 py-4 shadow-tarjeta transition-colors duration-150 ${acento ? 'border-ambar-200 hover:border-ambar-400' : 'border-gris-200 hover:border-marino-300'}`} data-id="element-1347" data-mp="3qitansqiygmn">
            <div className="flex items-start justify-between" data-id="element-1348" data-mp="2v9hktok6kgl5">
              <p className="text-xs font-medium text-gris-500" data-id="element-1349" data-mp="2qby3gfs6rnoz">{etiqueta}</p>
              <Icono className={`h-4 w-4 ${acento ? 'text-ambar-500' : 'text-gris-300'}`} aria-hidden="true" data-id="element-1350" data-mp="2lcv2b6fbu1ma" />
            </div>
            <p className={`tabular mt-2 text-3xl font-semibold ${acento ? 'text-ambar-600' : 'text-marino-900'}`} data-id="element-1351" data-mp="3e22a6w0o83k7">
              {valor}
            </p>
            <p className="mt-1 text-2xs text-gris-400" data-id="element-1352" data-mp="34c0jdwgxo71l">{detalle}</p>
          </Link>)}
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-3" data-id="element-1353" data-mp="2ze7fxb70lr50">
        <Panel className="xl:col-span-2" data-id="element-1354" data-mp="2zbcsrbwm9s7w">
          <EncabezadoPanel titulo="Vencimientos críticos" descripcion="Ordenados por urgencia. Rojo: 3 días o menos · Ámbar: hasta 10 días · Verde: en tiempo." accion={<Link to="/vencimientos" className="text-xs font-semibold text-marino-600 hover:text-marino-800" data-id="element-1356" data-mp="46ej747uu7cn">
                Ver todos
              </Link>} data-id="element-1355" data-mp="np4m7wg906k" />
          {criticos.length === 0 ? <EstadoVacio mensaje="Sin vencimientos vigentes en esta sucursal" sugerencia="Cambia el filtro de sucursal en la barra superior para revisar otras plazas." data-id="element-1357" data-mp="i4uocqq2jpso" /> : <ul className="divide-y divide-gris-100" data-id="element-1358" data-mp="r50xk0eqi3m1">
              {criticos.map(p => {
            const exp = expedientes.find(e => e.id === p.expedienteId);
            const dias = diasRestantes(p.fechaLimite);
            const responsable = abogadoPorId(p.responsableId);
            return <li key={p.id} data-id="element-1359" data-mp="15w9uyvovot9q">
                    <Link to={`/expedientes/${p.expedienteId}`} className="flex items-center gap-4 px-5 py-3 transition-colors duration-150 hover:bg-gris-50" data-id="element-1360" data-mp="15svs7mcayi1r">
                      <PuntoSemaforo nivel={nivelPorDias(dias)} data-id="element-1361" data-mp="g732tpa2sic4" />
                      <div className="min-w-0 flex-1" data-id="element-1362" data-mp="g4gnv4kuv0jv">
                        <p className="truncate text-sm font-medium text-gris-800" data-id="element-1363" data-mp="uwipou6hk3mj">{p.concepto}</p>
                        <p className="mt-0.5 truncate text-xs text-gris-500" data-id="element-1364" data-mp="q3pm49mr71c1">
                          <span className="font-mono text-marino-700" data-id="element-1365" data-mp="13vm9y1he2p2m">{exp?.localizador}</span>
                          {' · '}
                          {exp?.cliente}
                          {' · '}
                          {responsable?.nombre}
                        </p>
                      </div>
                      <div className="w-36 shrink-0 text-right" data-id="element-1366" data-mp="18uywsm89fw57">
                        <ChipDias fechaLimite={p.fechaLimite} data-id="element-1367" data-mp="18tdz4nqnfx34" />
                        <p className="tabular mt-1 text-2xs text-gris-400" data-id="element-1368" data-mp="3fm84563a1nep">
                          {fechaCorta(p.fechaLimite)}
                        </p>
                      </div>
                    </Link>
                  </li>;
          })}
            </ul>}
        </Panel>

        <Panel data-id="element-1369" data-mp="1v2ukqeykkikf">
          <EncabezadoPanel titulo="Expedientes prestados" descripcion="Salidas de almacén sin devolución" data-id="element-1370" data-mp="1v5gsaelgmy2n" />
          {prestados.length === 0 ? <EstadoVacio mensaje="Todos los expedientes están en su caja" data-id="element-1371" data-mp="1lbzzndx27p0k" /> : <ul className="divide-y divide-gris-100" data-id="element-1372" data-mp="17ci6yngn7rlx">
              {prestados.map(e => <li key={e.id} className="px-5 py-3" data-id="element-1373" data-mp="1bh81a7vnky4q">
                  <div className="flex items-center justify-between gap-2" data-id="element-1374" data-mp="xg5etu0qxlta">
                    <Link to={`/expedientes/${e.id}`} className="font-mono text-xs font-semibold text-marino-700 hover:underline" data-id="element-1375" data-mp="xi8cn0854j8k">
                      {e.localizador}
                    </Link>
                    <ChipMateria materia={e.materia} data-id="element-1376" data-mp="2wroipu7703w3" />
                  </div>
                  <p className="mt-1 text-xs text-gris-700" data-id="element-1377" data-mp="1jmen2yc8lkse">{e.prestadoA}</p>
                  <p className="tabular mt-0.5 text-2xs text-gris-400" data-id="element-1378" data-mp="1fkte3o2tpqnh">
                    Desde {fechaCorta(e.prestadoDesde!)} ·{' '}
                    {Math.abs(diasRestantes(e.prestadoDesde!))} días fuera
                  </p>
                  <p className="mt-1 text-2xs text-gris-400" data-id="element-1379" data-mp="15tyoqaua6axt">{rutaUbicacion(e)}</p>
                </li>)}
            </ul>}
        </Panel>
      </div>

      <Panel className="mt-4" data-id="element-1380" data-mp="vw2dzk599i08">
        <EncabezadoPanel titulo="Audiencias y citas de la semana" descripcion={`${audienciasSemana.length} eventos programados · ${abogados.filter(a => a.activo && a.rol !== 'Recepción/Archivo').length} abogados en agenda`} accion={<Link to="/calendario" className="text-xs font-semibold text-marino-600 hover:text-marino-800" data-id="element-1382" data-mp="2m7oudm11t79k">
              Abrir calendario
            </Link>} data-id="element-1381" data-mp="vvspy23rtkx6" />
        <MiniSemana audiencias={audiencias.filter(a => idsEnAlcance.has(a.expedienteId))} data-id="element-1383" data-mp="2oswj4pwhcyl1" />
      </Panel>
    </>;
}